import { useState, useCallback, useEffect } from 'react';
import { message, Modal, Typography } from 'antd';
import { deleteConversation, updateContact } from '../MessagesServices';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const useContactStatus = (selectedConversation, setConversations, setSelectedConversation) => {
    const { t } = useTranslation();
    const [isIgnored, setIsIgnored] = useState(selectedConversation?.contact?.is_ignored || false);
    const [isSnoozed, setIsSnoozed] = useState(selectedConversation?.contact?.is_snoozed || false);
    const [isAdmin, setIsAdmin] = useState(selectedConversation?.contact?.contact_type === 'admin' || false);
    const [ignoreLoading, setIgnoreLoading] = useState(false);
    const [snoozeLoading, setSnoozeLoading] = useState(false);
    const [adminLoading, setAdminLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const handleUpdateContact = useCallback(async (body) => {
        try {
            await updateContact(selectedConversation?.contact?.id, body);
            return true;
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to update contact');
            return false;
        }
    }, [selectedConversation]);

    useEffect(() => {
        if (selectedConversation) {
            setIsIgnored(selectedConversation.contact?.is_ignored || false);
            setIsSnoozed(selectedConversation.contact?.is_snoozed || false);
            setIsAdmin(selectedConversation.contact?.contact_type === 'admin' || false);
        }
    }, [selectedConversation]);

    const updateConversationList = useCallback((updateFn) => {
        if (!selectedConversation) return;

        setConversations(prevConvs => prevConvs.map(conv => {
            if (conv.id === selectedConversation.id) {
                return {
                    ...conv,
                    contact: {
                        ...conv.contact,
                        ...updateFn(conv.contact)
                    }
                };
            }
            return conv;
        }));
    }, [selectedConversation, setConversations]);

    const handleIgnore = useCallback(() => {
        Modal.confirm({
            title: t('confirm_ignore_title', { action: isIgnored ? 'Unignore' : 'Ignore' }),
            content: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Text>{t('confirm_ignore_content', { action: isIgnored ? 'unignore' : 'ignore' })}</Text>
                </div>
            ),
            onOk: async () => {
                setIgnoreLoading(true);
                try {
                    const success = await handleUpdateContact({ is_ignored: !isIgnored });
                    if (success) {
                        setIsIgnored(prev => !prev);
                        message.success(`Conversation ${!isIgnored ? 'ignored' : 'unignored'}`);
                        updateConversationList(contact => ({ ...contact, is_ignored: !isIgnored }));
                    }
                } finally {
                    setIgnoreLoading(false);
                }
            },
        });
    }, [isIgnored, handleUpdateContact, updateConversationList, t]);

    const handleSnooze = useCallback((duration) => {
        Modal.confirm({
            title: t('confirm_snooze_title'),
            content: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Text>{t('confirm_snooze_content', { duration })}</Text>
                </div>
            ),
            onOk: async () => {
                setSnoozeLoading(true);
                try {
                    const success = await handleUpdateContact({
                        is_snoozed: true,
                        snooze_duration: duration,
                    });
                    if (success) {
                        setIsSnoozed(true);
                        message.success(`Conversation snoozed for ${duration} seconds`);
                        updateConversationList(contact => ({
                            ...contact,
                            is_snoozed: true,
                            snooze_duration: duration
                        }));
                    }
                } finally {
                    setSnoozeLoading(false);
                }
            },
        });
    }, [handleUpdateContact, updateConversationList, t]);

    const handleMarkAsAdmin = useCallback(() => {
        Modal.confirm({
            title: t('confirm_admin_title', { action: isAdmin ? 'Unmark' : 'Mark' }),
            content: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Text>{t('confirm_admin_question', { action: isAdmin ? 'unmark' : 'mark' })}</Text>
                    <Text strong style={{ marginTop: '8px' }}>
                        {t('confirm_admin_important_note')}
                    </Text>
                </div>
            ),
            onOk: async () => {
                setAdminLoading(true);
                try {
                    const newContactType = isAdmin ? 'customer' : 'admin';
                    const success = await handleUpdateContact({ contact_type: newContactType });
                    if (success) {
                        setIsAdmin(prev => !prev);
                        message.success(`${isAdmin ? 'Unmarked' : 'Marked'} as admin`);
                        updateConversationList(contact => ({
                            ...contact,
                            contact_type: newContactType
                        }));
                    }
                } finally {
                    setAdminLoading(false);
                }
            },
        });
    }, [isAdmin, handleUpdateContact, updateConversationList, t]);

    const handleDelete = useCallback(() => {
        if (!selectedConversation) return;

        Modal.confirm({
            title: t('confirm_delete_conversation_title'),
            content: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Text>{t('confirm_delete_conversation_content')}</Text>
                </div>
            ),
            okText: t('delete'),
            okType: 'danger',
            onOk: async () => {
                setDeleteLoading(true);
                try {
                    await deleteConversation(selectedConversation.id);
                    setConversations(prev => {
                        const filteredConversations = prev.filter(conv => conv.id !== selectedConversation.id);
                        
                        // Find index of current conversation in the original array
                        const currentIndex = prev.findIndex(conv => conv.id === selectedConversation.id);
                        
                        // If there are other conversations, select one nearby
                        if (filteredConversations.length > 0) {
                            // Try to select the next conversation or the previous one if we're at the end
                            const nextIndex = currentIndex < filteredConversations.length ? currentIndex : currentIndex - 1;
                            // Make sure we have a valid index
                            const newSelectedIndex = Math.max(0, Math.min(nextIndex, filteredConversations.length - 1));
                            
                            // Select the new conversation
                            setTimeout(() => {
                                if (filteredConversations[newSelectedIndex]) {
                                    setSelectedConversation(filteredConversations[newSelectedIndex]);
                                }
                            }, 0);
                        }
                        
                        return filteredConversations;
                    });
                } finally {
                    setDeleteLoading(false);
                }
            }
        });
    }, [selectedConversation, setConversations, setSelectedConversation, t]);

    return {
        isIgnored,
        isSnoozed,
        isAdmin,
        updateContactStatus: {
            handleIgnore,
            handleSnooze,
            handleMarkAsAdmin,
            handleDelete,
            ignoreLoading,
            snoozeLoading,
            adminLoading,
            deleteLoading
        }
    };
};

export default useContactStatus;